'use client'

import type { ComponentProps } from 'react'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { XIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Dialog. Figma: 10 / Components / Dialog (Dialog/CreateGrowPage, DialogClose).
 * Raised surface, 12px corners, 32px padding, 20px gap, default border and the overlay shadow.
 * DialogClose is the 32px filled circle: Colors/action/primary with the foreground cross.
 */
const Dialog = DialogPrimitive.Root

function DialogTrigger({ className, ...props }: Omit<DialogPrimitive.Trigger.Props, 'className'> & { className?: string }) {
    return (
        <DialogPrimitive.Trigger
            data-slot='dialog-trigger'
            className={className}
            {...props}
        />
    )
}

function DialogOverlay({ className, ...props }: Omit<DialogPrimitive.Backdrop.Props, 'className'> & { className?: string }) {
    return (
        <DialogPrimitive.Backdrop
            data-slot='dialog-overlay'
            className={cn('fixed inset-0 z-50 bg-surface-scrim data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0', className)}
            {...props}
        />
    )
}

/* DialogClose: the GoodUnited round close control. */
function DialogCloseButton({ className, ...props }: Omit<DialogPrimitive.Close.Props, 'className'> & { className?: string }) {
    return (
        <DialogPrimitive.Close
            data-slot='dialog-close'
            aria-label='Close'
            className={cn('inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-action-primary text-action-primary-foreground transition-colors outline-none hover:bg-action-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus [&_svg]:size-4', className)}
            {...props}
        >
            <XIcon aria-hidden='true' />
        </DialogPrimitive.Close>
    )
}

type DialogContentProps = Omit<DialogPrimitive.Popup.Props, 'className'> & {
    className?: string
}

function DialogContent({ className, children, ...props }: DialogContentProps) {
    return (
        <DialogPrimitive.Portal>
            <DialogOverlay />
            <DialogPrimitive.Popup
                data-slot='dialog-content'
                className={cn(
                    'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-140 -translate-x-1/2 -translate-y-1/2 flex-col gap-5 overflow-y-auto rounded-xl border border-border-default bg-surface-overlay p-6 text-text-primary shadow-overlay outline-none sm:p-8 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                    className
                )}
                {...props}
            >
                {children}
            </DialogPrimitive.Popup>
        </DialogPrimitive.Portal>
    )
}

function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='dialog-header'
            className={cn('flex items-start justify-between gap-3', className)}
            {...props}
        />
    )
}

function DialogTitle({ className, ...props }: Omit<DialogPrimitive.Title.Props, 'className'> & { className?: string }) {
    return (
        <DialogPrimitive.Title
            data-slot='dialog-title'
            className={cn('font-heading text-2xl font-semibold text-text-primary', className)}
            {...props}
        />
    )
}

function DialogDescription({ className, ...props }: Omit<DialogPrimitive.Description.Props, 'className'> & { className?: string }) {
    return (
        <DialogPrimitive.Description
            data-slot='dialog-description'
            className={cn('text-base text-text-secondary', className)}
            {...props}
        />
    )
}

function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='dialog-footer'
            className={cn('flex flex-col gap-2 pt-1 *:w-full', className)}
            {...props}
        />
    )
}

const DialogClose = DialogPrimitive.Close

export {
    Dialog,
    DialogClose,
    DialogCloseButton,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogTitle,
    DialogTrigger
}
